import { useMemo } from 'react';
import { get } from 'lodash';

import { APPLICANT_ROLE_OPTIONS, FIELD_PATHS, FIELD_VALUES } from '@funnels/constants';
import { useFormValues } from '@funnels/hooks';
import { GuarantorRelationshipToBorrower, StoreApplicant } from '@funnels/types';

import { DropdownField } from '../../Fields/';

interface ApplicantRoleProps {
  applicantIndex: number;
  onApplicantRoleChange?: (value: string) => void;
}

export const ApplicantRole = ({ applicantIndex, onApplicantRoleChange, ...rest }: ApplicantRoleProps) => {
  const [formValues, setFormValue] = useFormValues();
  const applicantId = get(formValues, FIELD_PATHS.APPLICANT_ID(applicantIndex));
  const applicantRole = get(formValues, FIELD_PATHS.APPLICANT_TYPE(applicantIndex));
  const applicants: StoreApplicant[] = get(formValues, FIELD_PATHS.APPLICANTS(), []);
  // memoize guarantors who have relationship with current applicant if the applicant is a borrower
  const guarantorsWithRelationship = useMemo<Map<number, GuarantorRelationshipToBorrower>>(() => {
    if (applicantRole !== FIELD_VALUES.BORROWER) return new Map([]);
    return applicants.reduce((guarantors, applicant, index) => {
      if (
        get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_GUARANTOR_RELATIONSHIP_TO_BORROWER_APPLICANT_ID(0)) ===
        applicantId
      ) {
        guarantors.set(
          index,
          get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_GUARANTOR_RELATIONSHIP_TO_BORROWER(0))
        );
      }
      return guarantors;
    }, new Map<number, GuarantorRelationshipToBorrower>([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnChange = ([selectedValue]: string[]) => {
    onApplicantRoleChange && onApplicantRoleChange(selectedValue);

    if (selectedValue !== FIELD_VALUES.GUARANTOR) {
      // reset guarantorRelationshipToBorrower
      setFormValue(FIELD_PATHS.APPLICANT_GUARANTOR_RELATIONSHIP_TO_BORROWER(applicantIndex), undefined);
      // in case the user accidentally changes the role between guarantor and borrower, we will use the memoized
      // guarantors who have relationship with this applicant to revert the unset of guarantorRelationshipToBorrower
      // for other applicants, therefore they remain unchanged.
      guarantorsWithRelationship.forEach((relationship, index) => {
        // if there are any existing guarantors (memoized) who have relationship with current applicant,
        // revert the reset of guarantorRelationshipToBorrower that is performed in the else block
        setFormValue(FIELD_PATHS.APPLICANT_GUARANTOR_RELATIONSHIP_TO_BORROWER(index), relationship);
      });
    } else {
      // reset the guarantor relationship if other guarantor has relationship with this applicant whose role is changed
      // from borrower to guarantor
      applicants.forEach((_, index) => {
        if (
          get(formValues, FIELD_PATHS.APPLICANT_GUARANTOR_RELATIONSHIP_TO_BORROWER_APPLICANT_ID(index)) === applicantId
        ) {
          setFormValue(FIELD_PATHS.APPLICANT_GUARANTOR_RELATIONSHIP_TO_BORROWER(index), undefined);
        }
      });
    }
  };
  return (
    <DropdownField
      {...rest}
      label="Applicant role"
      name={FIELD_PATHS.APPLICANT_TYPE(applicantIndex)}
      options={APPLICANT_ROLE_OPTIONS}
      defaultValue={FIELD_VALUES.BORROWER}
      onChange={handleOnChange}
    />
  );
};
